import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Flex,
  HStack,
  Icon,
  Select,
  Stack,
  StackDivider,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { IoAddOutline } from "react-icons/io5";
import { FormattedMessage, useIntl } from "react-intl";
import useSWR from "swr";
import FormGrid from "../../../components/Form/FormGrid";
import HoldersSelectMultipleGrid from "../../../components/Form/HoldersSelectMultipleGrid";
import InputPhone from "../../../components/Form/InputPhone";
import ApiErrorDetailRHF from "../../../components/ReactHookForm/ApiErrorDetailRHF";
import FormController from "../../../components/ReactHookForm/FormController";
import FormObserver from "../../../components/ReactHookForm/FormObserver";
import { COUNTRIES_STATE_VALUE_ENABLED } from "../../../constants/countries";
import {
  CUSTOMERS_CONTACTS_VISIBILITY_LIST,
  CUSTOMERS_PARENTSHIPS_LIST,
} from "../../../constants/customers";
import {
  HOLDERS_STATES_LIST,
  HOLDERS_STATES_VALUE_DISABLED,
} from "../../../constants/holders";
import { useApi } from "../../../hooks/useApi";
import useApiFetcher from "../../../hooks/useApiFetcher";
import generateApiUri from "../../../libraries/utils/generateApiUri";

export const CUSTOMERS_FORM_CONTACTS_FIELDS = ["data.contacts"];

export const CUSTOMERS_FORM_CONTACTS_DEFAULT_VALUES = {
  first_name: "",
  last_name: "",
  phone_country: "FR",
  phone_number: "",
  parentship: "",
  pickup_holder: false,
  visibility: "public",
  is_restricted_by_holder: false,
  holder_id: [],
};

/**
 * @typedef {Object} Props
 * @property {"create" | "update"} context
 * @property {"customer" | "admin"} userContext
 * @property {number} [customerId]
 * @property {boolean} [withApiErrorDetail]
 *
 * @param {Props} props
 */
function CustomersFormContacts({
  context,
  userContext,
  customerId,
  withApiErrorDetail = false,
}) {
  const intl = useIntl();

  const { control } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "data.contacts",
  });

  const apiFetcher = useApiFetcher();

  const { data: countriesResponse } = useSWR(
    generateApiUri({
      id: "@countries.viewAny",
      query: {
        state: [COUNTRIES_STATE_VALUE_ENABLED],
        per_page: 250,
      },
    }),
    apiFetcher,
  );

  const handleClickAddContact = useCallback(() => {
    append(CUSTOMERS_FORM_CONTACTS_DEFAULT_VALUES);
  }, [append]);

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("../../../types/Holder").Holder[]>} */
  const { swrResponse } = useApi(
    customerId
      ? generateApiUri({
          id: "@holders.viewAny",
          query: {
            customer_id: customerId,
            state: HOLDERS_STATES_LIST.filter(
              (state) => state.id !== HOLDERS_STATES_VALUE_DISABLED,
            ).map((state) => state.id),
            fields: ["holder.id_card"],
          },
        })
      : null,
  );

  return (
    <Box>
      <Flex justify="flex-end">
        <Button
          onClick={handleClickAddContact}
          leftIcon={<Icon as={IoAddOutline} />}
          colorScheme="brandPrimary">
          <FormattedMessage defaultMessage="Ajouter une personne" />
        </Button>
      </Flex>

      {withApiErrorDetail && <ApiErrorDetailRHF py="1rem" />}

      <Stack spacing="1rem">
        {fields.length === 0 ? (
          <Box mt="1rem">
            <FormattedMessage defaultMessage="Aucune personne pour le moment. Ajoutez une personne en cliquant sur le bouton ci-dessus." />
          </Box>
        ) : (
          <Stack spacing="1rem" divider={<StackDivider />}>
            {fields.map((field, index) => (
              <Box key={field.id}>
                <Stack spacing="1rem" flexGrow={1}>
                  {userContext === "admin" && (
                    <FormGrid>
                      <FormController
                        control={control}
                        name={`data.contacts.${index}.visibility`}
                        label={intl.formatMessage({
                          defaultMessage: "Visibilité",
                        })}
                        renderWithFormControl={(field) => (
                          <Select {...field}>
                            {CUSTOMERS_CONTACTS_VISIBILITY_LIST.map(
                              (visibility, index) => (
                                <option
                                  value={visibility.value}
                                  key={visibility.value}>
                                  {intl.formatMessage(visibility.label)}
                                </option>
                              ),
                            )}
                          </Select>
                        )}
                      />
                    </FormGrid>
                  )}

                  <FormGrid minCellWidth="400px">
                    <FormController
                      name={`data.contacts.${index}.first_name`}
                      rules={{ required: true }}
                      label={intl.formatMessage({
                        defaultMessage: "Prénom",
                      })}
                    />

                    <FormController
                      name={`data.contacts.${index}.last_name`}
                      rules={{ required: true }}
                      label={intl.formatMessage({
                        defaultMessage: "Nom",
                      })}
                    />

                    <InputPhone
                      isRequired={true}
                      nameCountry={`data.contacts.${index}.phone_country`}
                      nameNumber={`data.contacts.${index}.phone_number`}
                      countries={countriesResponse?.data}
                    />

                    <FormController
                      name={`data.contacts.${index}.parentship`}
                      rules={{ required: true }}
                      label={intl.formatMessage({
                        defaultMessage: "Lien de parenté",
                      })}
                      renderWithFormControl={(field) => (
                        <Select {...field}>
                          <option value="">-</option>

                          {CUSTOMERS_PARENTSHIPS_LIST.map(
                            (parentship, index) => (
                              <option value={parentship.value} key={index}>
                                {intl.formatMessage(parentship.label)}
                              </option>
                            ),
                          )}
                        </Select>
                      )}
                    />
                  </FormGrid>

                  {userContext === "admin" && (
                    <FormController
                      name={`data.contacts.${index}.comment`}
                      type="textarea"
                      helperText={intl.formatMessage({
                        defaultMessage:
                          "Les commentaires ne sont visibles que par les administrateurs.",
                      })}
                      label={intl.formatMessage({
                        defaultMessage: "Commentaires",
                      })}
                    />
                  )}

                  {context === "update" && (
                    <Box>
                      <FormController
                        name={`data.contacts.${index}.is_restricted_by_holder`}
                        helperText={intl.formatMessage({
                          defaultMessage:
                            "Restreint cette personne à certains adhérents uniquement.",
                        })}
                        renderWithFormControl={(field) => (
                          <Checkbox
                            {...field}
                            isChecked={Boolean(field.value)}
                            colorScheme="brandPrimary">
                            <FormattedMessage defaultMessage="Restreindre" />
                          </Checkbox>
                        )}
                      />

                      <FormObserver
                        name={`data.contacts.${index}.is_restricted_by_holder`}>
                        {(isRestrictedByHolder) => (
                          <Collapse in={isRestrictedByHolder}>
                            <Box pt="1rem">
                              <FormController
                                control={control}
                                name={`data.contacts.${index}.holder_id`}
                                renderWithFormControl={(field) => (
                                  <HoldersSelectMultipleGrid
                                    holders={swrResponse.data?.data ?? []}
                                    {...field}
                                  />
                                )}
                              />
                            </Box>
                          </Collapse>
                        )}
                      </FormObserver>
                    </Box>
                  )}

                  <HStack>
                    <Box flexGrow={1}>
                      <FormController
                        name={`data.contacts.${index}.pickup_holder`}
                        helperText={intl.formatMessage({
                          defaultMessage:
                            "Autorise la personne à venir chercher un adhérent.",
                        })}
                        renderWithFormControl={(field) => (
                          <Checkbox
                            {...field}
                            isChecked={Boolean(field.value)}
                            colorScheme="brandPrimary">
                            <FormattedMessage defaultMessage="Autorisé(e)" />
                          </Checkbox>
                        )}
                      />
                    </Box>

                    <Button
                      onClick={() => {
                        remove(index);
                      }}
                      variant="outline"
                      colorScheme="red"
                      size="sm"
                      aria-label={intl.formatMessage({
                        defaultMessage: "Supprimer",
                      })}>
                      <FormattedMessage defaultMessage="Supprimer" />
                    </Button>
                  </HStack>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

export default CustomersFormContacts;
